import { Icon } from "./Icon";

// Code-built illustrative mockups of the HomeAligns app UI (teal gradient
// header, white rounded cards, mint icon tiles — matching the real app).
// Sample data shown here is illustrative, not real user data.

const Shell = ({ title, children, className = "" }) => (
  <div
    className={`relative w-[290px] shrink-0 rounded-[2.6rem] border-[6px] border-ink/90 bg-white shadow-2xl ${className}`}
    aria-hidden="true"
  >
    <div className="absolute left-1/2 top-2.5 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-ink/90" />
    <div className="overflow-hidden rounded-[2.1rem]">
      <div
        className="px-5 pb-5 pt-11 text-white"
        style={{ backgroundImage: "linear-gradient(125deg,#0A4D68,#148A9D)" }}
      >
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/15">
            <Icon name="Home" className="h-4 w-4" />
          </span>
          <span className="text-sm font-extrabold tracking-tight">HomeAligns</span>
        </div>
        <p className="mt-3 text-base font-bold leading-snug">{title}</p>
      </div>
      <div className="space-y-2.5 bg-mist/70 p-4">{children}</div>
    </div>
  </div>
);

const Row = ({ icon, title, sub, right }) => (
  <div className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-sm">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-mist text-teal-deep">
      <Icon name={icon} className="h-4.5 w-4.5" />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] font-bold text-ink">{title}</span>
      <span className="block truncate text-[11px] text-ink/55">{sub}</span>
    </span>
    {right}
  </div>
);

const Pill = ({ children, tone = "teal" }) => (
  <span
    className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
      tone === "teal" ? "bg-teal-mist text-teal-deep" : "bg-amber-100 text-amber-700"
    }`}
  >
    {children}
  </span>
);

export const AppMockup = ({ variant = "dashboard", className = "" }) => {
  if (variant === "dashboard") {
    return (
      <Shell title="Everything your home needs, aligned." className={className}>
        <Row icon="Building2" title="My Apartment" sub="2 BHK · Assets tracked" right={<Pill>Active</Pill>} />
        <Row icon="WashingMachine" title="Washing Machine" sub="Invoice saved" right={<Pill>Warranty</Pill>} />
        <Row icon="AirVent" title="Split AC" sub="Service due soon" right={<Pill tone="amber">Reminder</Pill>} />
        <div className="rounded-2xl bg-white p-3 shadow-sm">
          <p className="text-[11px] font-semibold text-ink/55">Estimated household value</p>
          <p className="mt-1 flex items-center gap-1 text-lg font-extrabold text-teal-deep">
            <Icon name="IndianRupee" className="h-4 w-4" /> Tracked in app
          </p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-mist">
            <div className="h-full w-2/3 rounded-full" style={{ backgroundImage: "linear-gradient(90deg,#0A4D68,#148A9D)" }} />
          </div>
        </div>
      </Shell>
    );
  }
  if (variant === "asset") {
    return (
      <Shell title="Asset details & value." className={className}>
        <Row icon="Refrigerator" title="Refrigerator" sub="Double door · Serial saved" right={<Pill>Docs</Pill>} />
        <div className="rounded-2xl bg-white p-3 shadow-sm">
          <p className="text-[11px] font-semibold text-ink/55">Depreciation & estimated value</p>
          <div className="mt-2 flex items-end gap-1.5">
            {[38, 52, 44, 62, 56, 70].map((h, i) => (
              <span
                key={i}
                className="w-full rounded-t-md bg-teal/70"
                style={{ height: `${h}px`, opacity: 0.35 + i * 0.12 }}
              />
            ))}
          </div>
          <p className="mt-2 text-[10px] text-ink/45">Estimates are indicative, not valuations</p>
        </div>
        <Row icon="FileText" title="Purchase invoice" sub="Stored privately · PDF" right={<Icon name="Lock" className="h-4 w-4 text-teal-deep" />} />
      </Shell>
    );
  }
  if (variant === "services") {
    return (
      <Shell title="Book trusted pros." className={className}>
        <Row icon="Wrench" title="Plumbing" sub="Approved pros in your city" right={<Icon name="ChevronRight" className="h-4 w-4 text-ink/40" />} />
        <Row icon="Zap" title="Electrical" sub="Approved pros in your city" right={<Icon name="ChevronRight" className="h-4 w-4 text-ink/40" />} />
        <Row icon="AirVent" title="AC Repair" sub="Approved pros in your city" right={<Icon name="ChevronRight" className="h-4 w-4 text-ink/40" />} />
        <div className="rounded-2xl p-3 text-white shadow-sm" style={{ backgroundImage: "linear-gradient(125deg,#0A4D68,#148A9D)" }}>
          <p className="flex items-center gap-2 text-[13px] font-bold">
            <Icon name="CalendarCheck" className="h-4 w-4" /> Booking confirmed
          </p>
          <p className="mt-1 text-[11px] text-white/75">You choose the provider — details shared only with them</p>
        </div>
      </Shell>
    );
  }
  return (
    <Shell title="Never miss a renewal." className={className}>
      <Row icon="ShieldCheck" title="Warranty — Split AC" sub="Expires soon" right={<Pill tone="amber">30 days</Pill>} />
      <Row icon="FileText" title="Home insurance" sub="Renewal reminder set" right={<Pill>Tracked</Pill>} />
      <Row icon="BellRing" title="RO filter service" sub="Maintenance due" right={<Pill tone="amber">Due</Pill>} />
    </Shell>
  );
};
